/**
 * ProductsContainer
 * --
 */
import React from 'react';
import ProductsPresenter from './ProductsPresenter';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import MessageAlert, {
  calculateRentalMonthlyPayment,
  extractApplicationPolicy,
  flattenTreeData,
  STORE_URL,
} from 'utils';
import { decrypt } from 'utils/Crypto';
import { useCreateCart, useUpdateCart } from 'hooks/CartHooks';
import { useCreateWishlist, useDeleteWishlist } from 'hooks/WishlistHooks';
import lodash from 'utils/Lodash';
import { handleSearchKeyword } from 'utils/SearchKeyword';

const TAP = [
  {
    key: 'DESC-comment',
    label: '판매인기순',
  },
  {
    key: 'ASC-finalPrice',
    label: '낮은가격순',
  },
  {
    key: 'DESC-finalPrice',
    label: '높은가격순',
  },
  {
    key: 'DESC-score',
    label: '상품평순',
  },
  {
    key: 'DESC-create',
    label: '등록일순',
  },
];

const ProductsContainer = (props) => {
  /* ===== ROUTE ===== */
  const { navigate, location, searchParams, screenSize } = props;
  const select = searchParams.get('category');
  const type = searchParams.get('type') ?? 'delivery';
  const classification = searchParams.get('classification');
  const keyword = searchParams.get('keyword');

  /* ===== Mutate ===== */
  const { createCart } = useCreateCart();
  const { updateCart } = useUpdateCart();
  const { createWishlist } = useCreateWishlist();
  const { deleteWishlist } = useDeleteWishlist();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const { cartList, wishlist } = useUserDataStack();
  const userDataAction = useUserDataAction();
  const { products, categories, shops, policies, promotion, settings } =
    useDefaultShopDataStack();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 선택 카테고리
  const [selectCategory, setSelectCategory] = React.useState(categories[0]);
  // Modal Data
  const [modalData, setModalData] = React.useState({
    type: '',
    data: null,
  });

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 장바구니/위시리스트 추가
   * --
   * @param {string} t
   * @returns
   */
  const handleSubmit = async (t = 'cart', d) => {
    let mutateData = {};
    switch (t) {
      case 'cart':
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
          cart_num: d.cartNum,
          shop_information_id: d.shopKey,
          product_option: d.product_option ?? null,
          cart_type: d?.type,
          ...d?.regular_cycle,
        };

        const filterSameProduct = cartList.filter(
          (cart) => cart.product_id === d.key
        );

        // NOTE: 장바구니에 같은 상품이 없을 경우 Create
        if (!filterSameProduct.length) {
          await handleMutate(createCart, mutateData).then((res) => {
            if (res.status !== 200) {
              MessageAlert.warning('장바구니 추가에 실패하였습니다.');
              return;
            }
            MessageAlert.success('장바구니에 추가되었습니다.');
            userDataAction.addData('cartList', res.data);
          });
        }
        // NOTE: 장바구니에 같은 상품이 있을 경우
        else {
          const isSame = cartList.find((cart) => {
            const {
              cart_num: cartListNum,
              is_fixed,
              created_at,
              updated_at,
              cart_id,
              forwarding_location_id,
              ...cartRest
            } = cart;

            const { cart_num: newValueNum, ...newValueRest } = mutateData;

            return lodash.isObjEqual(cartRest, newValueRest);
          });

          // NOTE: 같은 옵션의 상품이 있을 경우 수량 업데이트
          if (isSame) {
            mutateData = {
              ...isSame,
              cart_num: isSame.cart_num + d.cartNum,
            };

            await handleMutate(updateCart, mutateData).then((res) => {
              if (res.status !== 200) {
                MessageAlert.warning('장바구니 추가에 실패하였습니다.');
                return;
              }
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.updateData('cartList', isSame, res.data);
            });
          }
          // NOTE: 같은 옵션의 상품이 아닐 경우 장바구니 추가
          else {
            await handleMutate(createCart, mutateData).then((res) => {
              if (res.status !== 200) {
                MessageAlert.warning('장바구니 추가에 실패하였습니다.');
                return;
              }
              MessageAlert.success('장바구니에 추가되었습니다.');
              userDataAction.addData('cartList', res.data);
            });
          }
        }

        return;
      case 'wishlist':
        mutateData = {
          user_id: userInfo.user_id,
          product_id: d.key,
        };
        await handleMutate(createWishlist, mutateData).then((res) => {
          if (res.status !== 200) {
            MessageAlert.warning('찜목록에 추가에 실패하였습니다.');
            return;
          }
          MessageAlert.success('찜목록에 추가되었습니다.');
          userDataAction.addData('wishlist', res.data);
        });
        return;
      case 'remove':
        mutateData = wishlist.find(
          (wish) => wish.product_id === d.key && wish.type === 'WISH'
        );
        await handleMutate(deleteWishlist, mutateData.wishlist_id).then(
          (res) => {
            if (res.status !== 200) {
              MessageAlert.warning('찜목록에서 삭제하는데 실패하였습니다.');
              return;
            }
            MessageAlert.success('찜목록에서 삭제되었습니다.');
            userDataAction.removeData('wishlist', 'wishlist_id', mutateData);
          }
        );
        return;
      default:
        MessageAlert.warning(
          `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
        );
        return;
    }
  };

  /**
   * Mutate Function 실행
   * --
   * @param {function} mutateFn
   * @param {object} mutate
   * @returns
   */
  const handleMutate = async (mutateFn, mutate) => {
    try {
      const res = await mutateFn(mutate);

      setModalData({ type: '', data: null });
      return res;
    } catch (err) {
      console.error('[Product handleMutate] Error !! ', err);
      // MessageAlert.error(
      //   `${t === 'cart' ? '장바구니 추가' : '찜하기'}에 실패하였습니다`
      // );
      throw err;
    }
  };

  /**
   * 렌탈 디폴트 옵션 추출
   * --
   * 재귀적으로 JSON 데이터를 순회하면서,
   * 배열 내 객체에 `default` 프로퍼티가 있는 경우 true인 항목만 남깁니다.
   */
  const extractDefaultSelections = (data) => {
    if (Array.isArray(data)) {
      // 배열 내에서 default: true인 첫 번째 객체 선택
      const defaultItem = data.find(
        (item) => item && typeof item === 'object' && item.default === true
      );

      if (!defaultItem) return null; // default: true가 없으면 null 반환

      // default로 선택된 객체를 재귀적으로 처리 (내부 배열 포함)
      return extractDefaultSelections({ ...defaultItem });
    } else if (data !== null && typeof data === 'object') {
      const result = {};
      for (const key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
        if (key.endsWith('_description')) continue; // 설명 필드 제거

        // 배열이면 default 값이 있는 객체를 찾아 적용 (내부 배열도 재귀적으로 처리)
        if (Array.isArray(data[key])) {
          const selected = extractDefaultSelections(data[key]);
          if (selected !== null) result[key] = selected; // 선택된 값이 있다면 유지
        } else {
          result[key] = extractDefaultSelections(data[key]); // 재귀 처리
        }
      }
      return result;
    }
    return data;
  };

  /* ===== HOOKS ===== */
  /**
   * 카테고리 플랫 정제
   * --
   */
  const FlattenCategories = React.useMemo(() => {
    return flattenTreeData(categories);
  }, [categories]);

  /**
   * 상품 목록 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    const KEYS = new Set([]);
    const newProducts = products
      ?.filter((product) => {
        const t = type.toLocaleUpperCase();
        if (t === 'DELIVERY' || t === 'SUBSCRIPTION' || t === 'RENTAL') {
          return product.type === t;
        } else if (type === 'best' || type === 'new' || type === 'present') {
          return product[`is_${type}`] === 'Y';
          // return MainSetting[type].some(
          //   (a) => a.product_id === product.product_id
          // );
        }
      })
      .map((product) => {
        const {
          product_file,
          product_name,
          num,
          price,
          product_status,
          product_category_id,
          product_id,
          shop_information_id,
          created_at,
          average_score,
          review_count,
          tag,
          product_option,
          type: delivery_type,
          packaging_method,
          regular_information,
          rental_information,
          is_best,
          is_present,
          is_new,
        } = product;

        // 썸네일 추출
        const thumbnail =
          product_file.find((file) => file.type === 'THUMBNAIL') || null;

        // 태그 추출
        const tagList = tag.map((tag) => ({
          id: tag.tag_id,
          label: tag.tag_name,
        }));

        // 쇼핑몰 추출
        const shopData = shops.find(
          (shop) => shop.shop_information_id === shop_information_id
        );

        // 적용 정책 추출
        const applyPolicy = policies.filter((policy) =>
          policy.map_product_policy.some((map) => map.product_id === product_id)
        );
        // 우선순위에 따른 적용되는 정책 추출
        const applicationPolicy = applyPolicy.length
          ? extractApplicationPolicy(applyPolicy, price, promotion)
          : null;

        // 카테고리 key 추출
        const categoryKey =
          FlattenCategories.find(
            (category) => category.product_category_id === product_category_id
          ) ?? '';
        // 렌탈 default 옵션
        const rentalOptions =
          type === 'rental'
            ? extractDefaultSelections(rental_information)
            : null;

        //검색 필터 태그용
        const tagsKeys = tag?.reduce((acc, tag, index) => {
          const tagKeyName = `tagKey_${index}`;
          KEYS.add(tagKeyName);
          return {
            [tagKeyName]: tag?.tag_name,
            ...acc,
          };
        }, {});
        return {
          key: product_id,
          category: categoryKey.key,
          title: product_name,
          price:
            type === 'rental'
              ? calculateRentalMonthlyPayment(price, rentalOptions)
              : price,
          isSoldOut: num === 0 || product_status === 'SOLDOUT' ? 'Y' : 'N',
          src: !thumbnail ? null : `${STORE_URL}/${thumbnail.file_url}`,
          tags: tagList,
          brand: shopData ? shopData.shop_information_name : '',
          shopKey: shop_information_id,
          discount: applicationPolicy
            ? applicationPolicy.discount_type === 'R'
              ? applicationPolicy.apply_discount_rate
              : applicationPolicy.discount_price
            : 0,
          discount_type: applicationPolicy
            ? applicationPolicy.discount_type
            : 'W',
          finalPrice: applicationPolicy
            ? type === 'rental'
              ? calculateRentalMonthlyPayment(
                  price - applicationPolicy.discount_price,
                  rentalOptions
                )
              : price - applicationPolicy.discount_price
            : type === 'rental'
            ? calculateRentalMonthlyPayment(price, rentalOptions)
            : price,
          isWish: wishlist.some(
            (wish) => wish.product_id === product_id && wish.type === 'WISH'
          ),
          create: created_at,
          score: average_score,
          comment: review_count,
          options: product_option,
          type: delivery_type,
          policy: applicationPolicy,
          packaging: packaging_method,
          regularCycle:
            delivery_type === 'SUBSCRIPTION'
              ? regular_information?.regular_cycle
              : null,
          maxRegularCount:
            delivery_type === 'SUBSCRIPTION'
              ? regular_information?.max_regular_delivery_count
              : null,
          rentalInfo: rentalOptions,
          isBest: is_best,
          isNew: is_new,
          isPresent: is_present,
          category_name: categoryKey.product_category_name,
          ...tagsKeys,
        };
      });

    let productData = newProducts;
    if (classification) {
      const classType = classification.toLocaleUpperCase();
      switch (classType) {
        case 'PROMOTION':
          productData = newProducts.filter(
            (product) =>
              product.policy && product.policy.policy_type === 'PROMOTION'
          );
          break;
        case 'BRAND':
          const brandList = shops.filter((shop) => shop.is_brand === 'Y');

          productData = newProducts.filter((product) =>
            brandList.some(
              (brand) => brand.shop_information_id === product.shopKey
            )
          );
          break;
        default:
          break;
      }
    }
    return keyword
      ? handleSearchKeyword(productData, keyword, [
          'title',
          'category_name',
          ...Array.from(KEYS),
        ])
      : productData;

    // eslint-disable-next-line
  }, [
    products,
    shops,
    policies,
    FlattenCategories,
    wishlist,
    type,
    promotion,
    classification,
    keyword,
  ]);

  const PresentCategories = React.useMemo(() => {
    if (type !== 'present') return [];

    const filterCategories = FlattenCategories?.filter((category) =>
      ProductList?.some((product) => {
        const { category: cate } = product;
        const split = cate?.split('-');

        return split?.includes(category?.key);
      })
    );

    return filterCategories;
  }, [FlattenCategories, ProductList, type]);

  /**
   * 카테고리 정제
   * --
   */
  const CategoryList = React.useMemo(() => {
    const mapping = (cate) => {
      const { key, product_category_name, children = [] } = cate;

      return {
        key: key,
        title: product_category_name,
        children: children.map((child) => mapping(child)),
      };
    };

    const newCategories = categories.map((cate) => mapping(cate));

    return newCategories ?? [];
  }, [categories]);

  /**
   * 필터 아이템 정제
   * --
   */
  const FilterItems = React.useMemo(() => {
    const filterShop =
      classification === 'brand'
        ? shops.filter((shop) => shop.is_brand === 'Y')
        : shops;
    const brandFilterItems = filterShop.map((shop) => ({
      label: shop.shop_information_name,
      value: shop.shop_information_id,
    }));

    return [
      // {
      //   title: '배송',
      //   options: [
      //     { label: '무료배송', value: '무료배송' },
      //     { label: '업체배송', value: '업체배송' },
      //     { label: '정기배송', value: '정기배송' },
      //   ],
      //   multiple: true,
      //   key: '배송', // TODO
      //   type: 'include',
      // },
      {
        title: '포장타입',
        options: [
          { label: '일반', value: 'GENERAL' },
          { label: '냉장', value: 'REFRIGERATION' },
          { label: '냉동', value: 'FREEZING' },
        ],
        key: 'packaging',
        type: 'include',
      },
      {
        title: '가격',
        options: [
          { label: '10,000원 미만', value: [0, 10000] },
          { label: '10,000원 ~ 50,000원', value: [10000, 50000] },
          { label: '50,000원 ~ 100,000원', value: [50000, 100000] },
          { label: '100,000원 이상', value: [100000, 9999999999] },
        ],
        key: 'finalPrice',
        type: 'range',
      },
      {
        title: '브랜드',
        options: brandFilterItems,
        multiple: true,
        key: 'shopKey',
        type: 'include',
      },
    ];
  }, [shops, classification]);

  /**
   * 브랜드 데이터 정제
   * --
   */
  const BrandList = React.useMemo(() => {
    if (classification !== 'brand') return [];

    const filterBrands = shops.filter((shop) => shop.is_brand === 'Y');

    const newBrandList = filterBrands.map((brand) => {
      const { shop_information_file } = brand;

      const thumbnail = shop_information_file.find(
        (file) => file.type === 'THUMBNAIL'
      );

      return {
        ...brand,
        thumbnail: thumbnail ? `${STORE_URL}/${thumbnail.file_url}` : null,
      };
    });

    return newBrandList;
  }, [classification, shops]);

  /**
   * 정렬 아이템 정제
   * --
   */
  const SortItems = React.useMemo(() => {
    if (!settings) return TAP;

    const { product_standard, product_order_by } = settings;

    let standard = '';
    switch (product_standard) {
      case 'NAME':
        standard = `${product_order_by}-title`;
        break;
      case 'PRICE':
        standard = `${product_order_by}-price`;
        break;
      case 'REGISTER':
        standard = `${product_order_by}-create`;
        break;
      default:
        standard = `${product_order_by}-finalPrice`;
        break;
    }

    return [
      {
        key: standard,
        label: '추천순',
      },
      ...TAP,
    ];
  }, [settings]);

  /**
   * Query Key 카테고리 정제
   * --
   */
  React.useEffect(() => {
    const decodeKey = decrypt(decodeURIComponent(select));

    const findCategory = FlattenCategories.find(
      (category) => category.product_category_id === Number(decodeKey)
    );

    setSelectCategory(findCategory);
  }, [select, FlattenCategories]);

  /* ===== RENDER ===== */
  return (
    <>
      <ProductsPresenter
        navigate={navigate}
        location={location}
        user={userInfo}
        products={ProductList}
        categories={PresentCategories}
        categoryList={CategoryList}
        selectedCategory={selectCategory}
        modalData={modalData}
        cartList={cartList}
        filterItems={FilterItems}
        type={type}
        classification={classification}
        brand={BrandList}
        onSubmit={handleSubmit}
        setModalData={setModalData}
        sortItems={SortItems}
        screenSize={screenSize}
      />
    </>
  );
};

export default ProductsContainer;
